/**
 * API Utility for making HTTP requests with authentication
 */
import { logApiCall } from './performanceLogger';
import { getApiBaseUrl } from './apiBaseUrl';

class ApiService {
  constructor() {
    this.baseURL = getApiBaseUrl();
    this.maxConcurrent = 6;
    this.activeCount = 0;
    this.queue = [];
    this.inFlightGets = new Map();
    this.responseCache = new Map();
    this.cacheMaxEntries = 100;
    this.slowRequestThresholdMs = Number(process.env.REACT_APP_SLOW_API_MS || 500);
  }

  /**
   * Get authorization headers with Bearer token, tenant code, and selected role
   */
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    
    // Check if token exists
    if (!token) {
      throw new Error('Access token required');
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    // Add tenant code header if available
    const tenantCode = this.getCurrentTenantCode();
    if (tenantCode) {
      headers['X-Tenant-Code'] = tenantCode;
    }

    // Add selected role header if available (for data governance)
    const selectedRole = localStorage.getItem('selectedRole');
    if (selectedRole) {
      headers['X-Selected-Role'] = selectedRole;
    }

    return headers;
  }

  /**
   * Get current tenant code from localStorage or user context
   * Priority: selectedTenantCode (string) > selectedTenant (object) > JWT token
   */
  getCurrentTenantCode() {
    // First, try to get selectedTenantCode (string) - this is set when tenant is selected
    const selectedTenantCode = localStorage.getItem('selectedTenantCode');
    if (selectedTenantCode) {
      return selectedTenantCode;
    }

    // If selectedTenantCode is not set, try selectedTenant (full object)
    const selectedTenant = localStorage.getItem('selectedTenant');
    if (selectedTenant) {
      try {
        const tenant = JSON.parse(selectedTenant);
        // Check multiple possible field names for tenant code
        return tenant.repositoryCode || tenant.tenant_code || tenant.code;
      } catch (e) {
        // If parsing fails, treat as string
        return selectedTenant;
      }
    }

    // Fallback to JWT token if localStorage doesn't have it
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.tenantCode;
      } catch (e) {
        // Token decode failed, continue without tenant code
      }
    }

    return null;
  }

  /**
   * Handle API response (pass through durationMs and endpoint for perf logging)
   */
  async handleResponse(response, durationMs = 0, endpoint = '') {
    logApiCall(response.requestMethod || 'GET', endpoint, durationMs, response.ok);
    if (durationMs >= this.slowRequestThresholdMs) {
      // Browser-side visibility for slow endpoints during production troubleshooting.
      console.warn('[API] Slow request detected', {
        method: response.requestMethod || 'GET',
        endpoint,
        durationMs,
        status: response.status
      });
    }
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let serverDetail = null;
      try {
        const errorData = await response.json();
        serverDetail = errorData.error || null;
        // For 5xx, prefer backend 'error' (actual exception message) so user sees the real cause
        if (response.status >= 500 && errorData.error) {
          errorMessage = errorData.error;
        } else {
          errorMessage = errorData.message || errorData.error || errorMessage;
        }
      } catch {
        // If response is not JSON, use the default error message
      }
      const err = new Error(errorMessage);
      err.status = response.status;
      err.serverError = serverDetail;
      throw err;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    return await response.text();
  }

  _enqueue(task) {
    return new Promise((resolve, reject) => {
      const run = () => {
        this.activeCount += 1;
        Promise.resolve()
          .then(task)
          .then(resolve, reject)
          .finally(() => {
            this.activeCount -= 1;
            this._drainQueue();
          });
      };
      if (this.activeCount < this.maxConcurrent) {
        run();
      } else {
        this.queue.push(run);
      }
    });
  }

  _drainQueue() {
    while (this.activeCount < this.maxConcurrent && this.queue.length > 0) {
      const next = this.queue.shift();
      next();
    }
  }

  _buildCacheKey(url, headers, cacheKeyOverride) {
    if (cacheKeyOverride) return cacheKeyOverride;
    const tenantCode = headers['X-Tenant-Code'] || '';
    const selectedRole = headers['X-Selected-Role'] || '';
    return `${url}|tenant=${tenantCode}|role=${selectedRole}`;
  }

  _getCached(cacheKey) {
    const entry = this.responseCache.get(cacheKey);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.responseCache.delete(cacheKey);
      return null;
    }
    return entry.data;
  }

  _setCached(cacheKey, data, ttlMs) {
    if (!ttlMs) return;
    this.responseCache.set(cacheKey, {
      data,
      expiresAt: Date.now() + ttlMs
    });
    if (this.responseCache.size > this.cacheMaxEntries) {
      const firstKey = this.responseCache.keys().next().value;
      this.responseCache.delete(firstKey);
    }
  }

  /**
   * Make GET request
   */
  async get(endpoint, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const headers = this.getAuthHeaders();
    const cacheKey = this._buildCacheKey(url, headers, options.cacheKey);
    const cacheTtlMs = options.cacheTtlMs || 0;
    const dedupe = options.dedupe !== false && !options.signal;

    if (cacheTtlMs) {
      const cached = this._getCached(cacheKey);
      if (cached) {
        return cached;
      }
    }

    if (dedupe && this.inFlightGets.has(cacheKey)) {
      return this.inFlightGets.get(cacheKey);
    }

    const fetchPromise = this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'GET',
        headers,
        ...options
      });
      response.requestMethod = 'GET';
      const durationMs = Date.now() - start;
      const data = await this.handleResponse(response, durationMs, endpoint);
      this._setCached(cacheKey, data, cacheTtlMs);
      return data;
    }).finally(() => {
      this.inFlightGets.delete(cacheKey);
    });

    if (dedupe) {
      this.inFlightGets.set(cacheKey, fetchPromise);
    }

    return fetchPromise;
  }

  /**
   * Make POST request
   */
  async post(endpoint, data = null, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    return this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      response.requestMethod = 'POST';
      const durationMs = Date.now() - start;
      return this.handleResponse(response, durationMs, endpoint);
    });
  }

  /**
   * Make PUT request
   */
  async put(endpoint, data = null, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    return this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      response.requestMethod = 'PUT';
      const durationMs = Date.now() - start;
      return this.handleResponse(response, durationMs, endpoint);
    });
  }

  /**
   * Make DELETE request
   */
  async delete(endpoint, data = null, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    return this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      response.requestMethod = 'DELETE';
      const durationMs = Date.now() - start;
      return this.handleResponse(response, durationMs, endpoint);
    });
  }

  /**
   * Make PATCH request
   */
  async patch(endpoint, data = null, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    return this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
        ...options
      });
      response.requestMethod = 'PATCH';
      const durationMs = Date.now() - start;
      return this.handleResponse(response, durationMs, endpoint);
    });
  }

  /**
   * Upload file with FormData
   */
  async uploadFile(endpoint, formData, options = {}) {
    const url = `${this.baseURL}/api${endpoint}`;
    const token = localStorage.getItem('token');
    
    const headers = {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type for FormData, let browser set it with boundary
    };

    // Add tenant code header if available
    const tenantCode = this.getCurrentTenantCode();
    if (tenantCode) {
      headers['X-Tenant-Code'] = tenantCode;
    }

    // Add selected role header if available (for data governance)
    const selectedRole = localStorage.getItem('selectedRole');
    if (selectedRole) {
      headers['X-Selected-Role'] = selectedRole;
    }
    
    return this._enqueue(async () => {
      const start = Date.now();
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        ...options
      });
      response.requestMethod = 'POST';
      const durationMs = Date.now() - start;
      return this.handleResponse(response, durationMs, endpoint);
    });
  }
}

// Create and export a singleton instance
export const api = new ApiService();

// Export the class for potential extension
export default ApiService;